import { Component, Vue } from 'vue-property-decorator';
import { ContainerForm, Hint, Title } from '@/components';
import { CreateElement, VNode, VNodeData } from 'vue';
import { formMixins } from '@/components/SignUpForm/mixins';
import { load } from 'recaptcha-v3';
import { mapActions } from 'vuex';
import { MapActions, MapGetters } from '@/utils';
import { Route } from 'vue-router';
import {
  Rules,
  IAuthorization,
  IRegistrationTranslation,
  VuetifyInput,
  LoginCredentialsType,
  Errors,
  ILoginForm } from '@/types';
import { VTextField } from 'vuetify/lib';
import './style.styl';

@Component({
  name: 'LoginForm',
  computed: {
    t(): IAuthorization { return (this.translations as IRegistrationTranslation).login; },
    form() {
      return {
        login: this.login,
        password: this.password,
      };
    },
    fieldsErrors() {
      return !!this.$store && !!this.$store.getters
        && !!this.$store.getters.translations
          && this.$store.getters.translations.field_errors;
    },
  },
  methods: {
    ...mapActions('accounts', {
      loginIn: 'login',
      set2faCode: 'set2faCode',
    }),
    ...mapActions('accounts', ['setUserPhoneEntered']),
  },
  data() {
    return {
      errorMessages: '',
      hasErrors: true,
      login: null,
      loginError: '',
      password: null,
      passwordError: '',
    };
  },
})
export default class LoginForm extends Vue implements ILoginForm {
  public bucket: Errors[] = [];
  public errorMessageSupport;
  public hasErrors;
  public formRules: Rules = formMixins.rules;
  public setUserPhoneEntered;
  public showPassword: boolean = false;
  public t!: IAuthorization;
  public errorBucket: Map<string, string[]> = new Map();

  @MapGetters(['translations'], 'accounts')
  @MapGetters(['errorMessageSupport'])

  @MapActions(['showCentralAlert'])

  public loginFomValidate() {
    this.errorBucket = new Map();
    Object.keys(this.form).forEach((f) => {
      if (!(this.$refs[f] as VuetifyInput).validate(true)) {
        this.errorBucket.set(f, (this.$refs[f] as VuetifyInput).errorBucket);
      }
    });
  }

  public checkErrors() {
    const bucket: boolean[] = [];
    this.errorBucket = new Map();
    Object.keys(this.form).forEach((f) => {
      if (this.$refs[f]) {
        bucket.push((this.$refs[f] as VuetifyInput).validate(false));
        this.hasErrors = bucket.some((x) => !x);
      }
    });
  }

  public async userLogin() {
    this.loginFomValidate();
    if (this.errorBucket.size > 0) {
      return;
    }
    const recaptcha = await load(process.env.VUE_APP_RECAPTHA_V3_TOKEN as string, {
      autoHideBadge: true,
    });
    try {
      this.hasErrors = true;
      const token: string = await recaptcha.execute('login');
      const credentials: LoginCredentialsType = {
        login: this.login,
        password: this.password,
        recaptcha: token,
      };
      const response = await this.loginIn(credentials);
      if (response.status >= 400 && response.status < 500) {
        this.errorBucket = new Map();
        const { mobile_errors }: { mobile_errors: Errors[] } = response.data;
        mobile_errors.forEach((error) => {
          this.errorBucket.set(error.field, error.messages);
        });
        this.hasErrors = false;
      }
      if (response && response.status === 200) {
        if (this.$route.meta.from.name !== 'login') {
          this.$router.pushBack();
        } else {
          await this.$router.push({path: '/'});
        }
        this.$gtm.trackEvent({
          eventCategory: 'login',
          eventAction: 'click',
          eventLabel: 'login_success',
          event: 'login_success',
        });
        this.hasErrors = false;
      }
      if (response && response.status === 206) {
        this.setUserPhoneEntered(response.data.masked_phone);
        this.set2faCode(response.data.id);
        this.$router.pushAndSave({
          name: 'confirm2fa',
        });
        this.hasErrors = false;
      }
    } catch (error) {
      console.error(error);
    }
  }

  private render(createElement: CreateElement): VNode {
    const title = createElement(Title, {
      props: {
        tag: 'h4',
        text: this.t.title,
      },
    });

    const link: VNode = createElement(Hint, {
      props: {
        text: this.t.hint,
        linkTo: 'signUp',
      },
      style: {
        'align-self': 'center',
        'margin-top': '19px',
      },
      slot: 'bottom',
    });

    const login: VNode = createElement(VTextField, {
      attrs: {
        name: 'login',
      },
      props: {
        validateOnBlur: true,
        id: 'login',
        height: 28,
        label: this.t.loginLabel,
        error: this.errorBucket.has('login'),
        errorMessages: this.errorBucket.get('login'),
        type: 'text',
        rules: [this.formRules.emailOrPhone(this.login || '', {
          required: !!this.fieldsErrors && this.fieldsErrors.required,
          phone: !!this.fieldsErrors && this.fieldsErrors.phone,
          email: {
            at: this.fieldsErrors?.email,
            user: this.fieldsErrors?.email,
            domain: this.fieldsErrors?.email,
          },
        })],
      },
      nativeOn: {
        input: (event) => {
          this.login = event.target.value.trim();
          this.$nextTick(() => {
            this.checkErrors();
          });
        },
      },
      style: {
        'margin-top': '17px',
        height: '60px',
      },
      class: {
        'input-wrapper': true,
      },
      ref: 'login',
    });

    const password: VNode = createElement(VTextField, {
      props: {
        validateOnBlur: true,
        'browser-autocomplete': 'current-password',
        error: this.errorBucket.has('password'),
        errorMessages: this.errorBucket.get('password'),
        height: 28,
        name: 'password',
        label: this.t.passwordLabel,
        'append-icon': this.showPassword ? '$vuetify.icons.visible' : '$vuetify.icons.visible_off',
        type: this.showPassword ? 'text' : 'password',
        rules: [this.formRules.required(this.password as string, this.fieldsErrors.required)],
      },
      attrs: {
        id: 'password',
        password: 'password',
      },
      style: {
        height: '60px',
      },
      nativeOn: {
        input: (event) => {
          this.password = event.target.value.trim();
          this.$nextTick(() => {
            this.checkErrors();
          });
        },
      },
      on: {
        'click:append': () => this.showPassword = !this.showPassword,
      },
      class: {
        'input-wrapper': true,
      },
      ref: 'password',
    });

    const button: VNode = createElement('button', {
      attrs: {
        type: 'submit',
        disabled: this.hasErrors,
      },
      style: {
        'margin-top': '30px',
      },
      class: [
        this.$vuetify.breakpoint.xsOnly
          ? 'orange-big-button__mobile'
          : 'orange-big-button',
      ],
      on: {
        click: (e) => {
          e.preventDefault();
          this.userLogin();
        },
      },
    }, this.t.buttonText);

    const rememberPasswordLink = createElement('span', {
      class: {
        'flex-center': true,
        'custom-link': true,
      },
      on: {
        'click': (): Promise<Route> => this.$router.push({name: 'passRecover'}),
      },
    }, this.t.forgotPassword);

    const data: VNodeData = {
      props: {
        customCardStyle: {
          'padding-bottom': '30px',
        },
      },
      domProps: {
        id: 'login',
      },
    };

    return createElement(ContainerForm, data,
      [
        title,
        login,
        password,
        button,
        rememberPasswordLink,
        link,
      ],
    );
  }
}
